import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCurrentUser } from '../contexts/UserContext';
import { isHostRole } from '../utils/roles';
import { getLKMeetingByToken, joinLKByToken } from '../services/livekitService';

// ---------------------------------------------------------------------------
// JoinPage — pure lobby/entry gate. Zero video rendering here.
//
// Responsibilities:
//   - Load meeting metadata from join token
//   - Demo sessions: collect guest name + email (no account required)
//   - Class sessions: require login, redirect to auth if missing
//   - Call join API, handle "not started yet" polling
//   - Navigate to /classroom-lk with all credentials
//
// ClassroomLKPage owns everything once the room is live.
// ---------------------------------------------------------------------------

function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function BrandPill() {
  return (
    <div className="brand-pill" style={{ marginBottom: '1.5rem' }}>
      <span className="brand-icon">M</span>
      <span className="brand-name">Minversity</span>
    </div>
  );
}

function MeetingCard({ meeting }) {
  const mentor = (meeting.participants || []).find((p) => p.role === 'mentor');
  const student = (meeting.participants || []).find((p) => p.role === 'student');
  return (
    <div className="join-meeting-card">
      <p className="join-meeting-type">
        {meeting.type === 'demo' ? 'Demo session' : 'Class session'}
        {meeting.status === 'live' && (
          <span className="live-badge" style={{ marginLeft: '0.5rem' }}>LIVE</span>
        )}
      </p>
      <h2 className="join-meeting-title">{meeting.title}</h2>
      <p className="join-meeting-time">
        {formatTime(meeting.scheduledAt)} · {meeting.duration} min
      </p>
      {(mentor || student) && (
        <div className="join-participants">
          {mentor?.name && <span className="join-participant-pill">Mentor: {mentor.name}</span>}
          {student?.name && <span className="join-participant-pill">Student: {student.name}</span>}
        </div>
      )}
    </div>
  );
}

export default function JoinPage() {
  const { token: joinToken } = useParams();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const userId = currentUser?.id || currentUser?._id || null;
  const isHost = isHostRole(currentUser?.role);

  const [meeting, setMeeting] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [loadingMeeting, setLoadingMeeting] = useState(true);

  const [guestName, setGuestName] = useState(
    currentUser ? `${currentUser.firstName || currentUser.name || ''}`.trim() : '',
  );
  const [guestEmail, setGuestEmail] = useState('');

  const [phase, setPhase] = useState('idle'); // idle | joining | waiting | error
  const [joinError, setJoinError] = useState('');

  const pollRef = useRef(null);

  // ── Load meeting metadata ─────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoadingMeeting(true);
    getLKMeetingByToken(joinToken)
      .then((data) => { if (!cancelled) setMeeting(data); })
      .catch((err) => { if (!cancelled) setLoadError(err.message || 'Session not found.'); })
      .finally(() => { if (!cancelled) setLoadingMeeting(false); });
    return () => { cancelled = true; };
  }, [joinToken]);

  // ── Navigate to classroom with all credentials ────────────────────────────
  const goToClassroom = useCallback((data, name, email) => {
    navigate('/classroom-lk', {
      state: {
        classInfo: {
          id: data.meetingId,
          title: data.title,
          detail: '',
          participants: meeting?.participants || [],
        },
        lkToken: data.token,
        lkServerUrl: data.serverUrl,
        sessionId: data.sessionId,
        meetingType: meeting?.type,
        isGuest: !userId,
        guestName: name || undefined,
        guestEmail: email || undefined,
      },
    });
  }, [navigate, meeting, userId]);

  // ── Auto-join for logged-in users once meeting data is ready ─────────────
  useEffect(() => {
    if (!meeting || loadingMeeting) return;
    if (meeting.type === 'class' && !userId) return; // must sign in
    if (phase !== 'idle') return;
    if (meeting.type === 'demo' && !userId && !guestName.trim()) return; // wait for form
    doJoin();
  }, [meeting, loadingMeeting]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Join API call → always navigates to /classroom-lk ────────────────────
  const doJoin = useCallback(async (nameOverride, emailOverride) => {
    setJoinError('');
    setPhase('joining');
    const name = nameOverride || guestName || undefined;
    const email = emailOverride || guestEmail || undefined;
    try {
      const data = await joinLKByToken({
        joinToken,
        userId: userId || undefined,
        guestName: name,
        isHost,
      });
      goToClassroom(data, name, email);
    } catch (err) {
      if (err.code === 'not_started') {
        setPhase('waiting');
        startPolling(name, email);
      } else {
        setJoinError(err.message || 'Failed to join.');
        setPhase('idle');
      }
    }
  }, [joinToken, userId, guestName, guestEmail, isHost, goToClassroom]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Poll until host starts, then navigate ─────────────────────────────────
  const startPolling = useCallback((name, email) => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const data = await joinLKByToken({
          joinToken,
          userId: userId || undefined,
          guestName: name,
          isHost: false,
        });
        clearInterval(pollRef.current);
        pollRef.current = null;
        goToClassroom(data, name, email);
      } catch (err) {
        if (err.code !== 'not_started') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setJoinError(err.message || 'Failed to join.');
          setPhase('idle');
        }
      }
    }, 10000);
  }, [joinToken, userId, goToClassroom]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleGuestSubmit = (e) => {
    e.preventDefault();
    if (guestName.trim() && guestEmail.trim()) doJoin(guestName.trim(), guestEmail.trim());
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loadingMeeting) {
    return (
      <div className="join-shell">
        <div className="join-card">
          <BrandPill />
          <p className="muted-text">Loading session…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="join-shell">
        <div className="join-card">
          <BrandPill />
          <p className="error-text">{loadError}</p>
        </div>
      </div>
    );
  }

  // ── Class session — not logged in ─────────────────────────────────────────
  if (meeting.type === 'class' && !userId) {
    return (
      <div className="join-shell">
        <div className="join-card">
          <BrandPill />
          <MeetingCard meeting={meeting} />
          <p className="muted-text" style={{ margin: '1.25rem 0 1.5rem' }}>
            Sign in to your Minversity account to join this session.
          </p>
          <button
            type="button"
            className="primary-btn"
            onClick={() => navigate(`/?return=/join/${joinToken}`)}
          >
            Sign in to join
          </button>
        </div>
      </div>
    );
  }

  // ── Demo session — guest name + email form ────────────────────────────────
  if (meeting.type === 'demo' && !userId && phase === 'idle') {
    return (
      <div className="join-shell">
        <div className="join-card">
          <BrandPill />
          <MeetingCard meeting={meeting} />
          <form onSubmit={handleGuestSubmit} style={{ marginTop: '1.5rem' }}>
            <div className="form-field">
              <label className="field-label" htmlFor="join-guest-name">Your name</label>
              <input
                id="join-guest-name"
                type="text"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="Enter your name"
                required
                autoFocus
              />
            </div>
            <div className="form-field" style={{ marginTop: '0.75rem' }}>
              <label className="field-label" htmlFor="join-guest-email">Email address</label>
              <input
                id="join-guest-email"
                type="email"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>
            {joinError && <p className="error-text" style={{ marginTop: '0.5rem' }}>{joinError}</p>}
            <button
              type="submit"
              className="primary-btn"
              style={{ marginTop: '1rem', width: '100%' }}
              disabled={!guestName.trim() || !guestEmail.trim()}
            >
              Join session
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Joining / waiting / error states ──────────────────────────────────────
  return (
    <div className="join-shell">
      <div className="join-card">
        <BrandPill />
        <MeetingCard meeting={meeting} />
        {phase === 'joining' && (
          <p className="muted-text" style={{ marginTop: '1.5rem' }}>Connecting…</p>
        )}
        {phase === 'waiting' && (
          <p className="muted-text" style={{ marginTop: '1.5rem' }}>
            Waiting for the host to start… checking every 10 seconds.
          </p>
        )}
        {joinError && (
          <p className="error-text" style={{ marginTop: '1rem' }}>{joinError}</p>
        )}
      </div>
    </div>
  );
}
